// src/modules/app-views/account/pages/Subscription/modals/RemoveSubscriptionModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React, { useEffect, useRef } from 'react';
import { Alert, ConfigProvider, theme } from 'antd';
import { DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import useZustand from 'hooks/useZustand';
import appService from 'services/appService';
import authService from 'services/authService';
import { darkTheme, lightTheme } from 'configs/appEngine';
import { markIntentionalDeletion } from 'hooks/useApiInitializer';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const RemoveSubscriptionModal = ({
  show,
  setShow,
  afterClose,
  navigate,
  // Optional: custom navigation after deletion
  backToPage, // e.g., 'init' - if not provided, uses navigation history
  backToQuery, // e.g., { tab: 'subscriptions' }
  // Context props passed from actionButton via openModal
  // NOTE: ActionButton passes 'page' not 'currentPage' in buildHandlerContext()
  page,
  moduleContext,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedUser = useZustand('auth', 'user');
  const storedTeam = useZustand('auth', 'team');
  const { del, data } = useZustand('global', 'useApi') || {};
  const currentTheme = useZustand('theme', 'currentTheme');
  const isDarkTheme = currentTheme === 'dark';

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const modalControllerRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // Try to get docId from props first (context passed from actionButton), fallback to Zustand
  const docId = page?.data?._id || data?._id || null;
  const subscriptionName = data?.organization || data?.name || '';
  const isLastSubscription = storedUser?.subscriptions?.length === 1;
  const hasTeam = storedTeam.find(t => t?.accountId !== storedUser?._id);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // If the user has team, show warning modal instead
  useEffect(() => {
    if (show && hasTeam) {
      appService.modal({
        flag: 'warning',
        title: 'remove_account_prohibit_title',
        content: (
          <div>
            <p>{translateX('remove_subscription_info')}</p>
          </div>
        ),
        okText: 'close',
        centered: true,
        mask: { closable: true },
      });
      setShow(false);
    }
  }, [show, hasTeam, setShow]);

  // Main modal effect - using appService.modal for proper dark mode support
  useEffect(() => {
    if (!show || hasTeam) {
      // Close modal if show is false
      if (modalControllerRef.current) {
        modalControllerRef.current.close();
        modalControllerRef.current = null;
        afterClose?.();
      }
      return;
    }

    const handleConfirm = async () => {
      const setPageStore = useZustand('module', 'setPage', false);
      const setPageBackStore = useZustand('module', 'setPageBack', false);
      const setLoadingPageStore = useZustand('boot', 'setLoadingPage', false);
      const setLoadingAppStore = useZustand('boot', 'setLoadingApp', false);

      if (!docId) {
        await appService.message({ flag: 'e', text: 'operation_not_allowed' });
        return;
      }

      try {
        // Close modal immediately
        if (modalControllerRef.current) {
          modalControllerRef.current.close();
          modalControllerRef.current = null;
        }
        setShow(false);

        // 1. Show loading screen (app loading for last subscription, page loading otherwise)
        if (isLastSubscription) {
          setLoadingAppStore({
            visible: true,
            title: 'removing_your_subscription',
            translucid: false,
          });
        } else {
          setLoadingPageStore({
            visible: true,
            title: 'removing_subscription',
            translucid: false,
          });
        }

        // 2. Mark this as an intentional deletion to suppress "document not found" notification
        // This prevents the notification from appearing when navigating away from the deleted document
        markIntentionalDeletion();

        // 3. Delete subscription
        await del({
          _id: docId,
          showSuccessNotification: false,
          showErrorNotification: false,
          noFetchDataStore: true,
          ignoreLoadingHide: true,
          headers: { 'x-forcepermanentlydeletation': true },
        });

        // 4. Refresh user account data
        await authService?.signInRefresh?.();

        // 5. Navigate: use custom navigation or fall back to navigation history
        if (backToPage) {
          // Custom navigation to specific page
          setPageStore({
            navigate,
            pageName: backToPage,
            query: backToQuery || {},
          });
        } else {
          // Use navigation history stack
          setPageBackStore({ navigate });
        }

        // 6. Show success message (after navigation starts)
        setTimeout(() => {
          appService.message({ flag: 's', text: 'subscription_removed_success' });
        }, 300);
      } catch (e) {
        console.error('[RemoveSubscriptionModal] Error:', e);

        // Hide loading on error
        if (isLastSubscription) {
          setLoadingAppStore({ visible: false, extendedDelayToHide: 0 });
        } else {
          setLoadingPageStore({ visible: false, extendedDelayToHide: 0 });
        }

        await appService.message({ flag: 'e', text: 'subscription_removed_failure' });
      }
    };

    const handleCancel = () => {
      if (modalControllerRef.current) {
        modalControllerRef.current.close();
        modalControllerRef.current = null;
      }
      setShow(false);
      afterClose?.();
    };

    // Create modal using appService.modal which uses App.useApp() internally (dark mode support)
    modalControllerRef.current = appService.modal({
      flag: 'confirm',
      title: translateX('remove_subscription_title'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>{translateX('remove_subscription_description')}</p>
        </div>
      ),
      okText: 'yes',
      cancelText: 'no',
      onOk: handleConfirm,
      onCancel: handleCancel,
      okButtonProps: { danger: true, icon: <DeleteOutlined /> },
      centered: true,
      mask: { closable: true },
    });

    // Cleanup on unmount
    return () => {
      if (modalControllerRef.current) {
        modalControllerRef.current.close();
        modalControllerRef.current = null;
      }
    };
  }, [
    show,
    hasTeam,
    docId,
    subscriptionName,
    isLastSubscription,
    currentTheme,
    isDarkTheme,
    del,
    navigate,
    backToPage,
    backToQuery,
    setShow,
    afterClose,
  ]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. EFFECTS II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  // Modal is rendered via appService.modal, so no JSX needed here
  return null;
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default RemoveSubscriptionModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************
